import { Input } from '@/components/ui/input';
import { extractErrorMessage } from '@/lib/errors';
import { cn } from '@/lib/utils';
import { useEffect, useRef, useState } from 'react';

interface InlineNumberFieldProps {
  value: number | null;
  onSave: (next: number | null) => Promise<void> | void;
  min?: number;
  max?: number;
  placeholder?: string;
  emptyLabel?: string;
  disabled?: boolean;
  className?: string;
  renderDisplay?: (value: number | null) => React.ReactNode;
}

export function InlineNumberField({
  value,
  onSave,
  min = 0,
  max,
  placeholder = 'Click to edit',
  emptyLabel = '—',
  disabled = false,
  className,
  renderDisplay,
}: InlineNumberFieldProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value !== null ? String(value) : '');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const commitGuard = useRef(false);

  useEffect(() => {
    if (!isEditing) setDraft(value !== null ? String(value) : '');
  }, [value, isEditing]);

  const commit = async () => {
    if (commitGuard.current) return;
    commitGuard.current = true;
    setError(null);

    const trimmed = draft.trim();
    let next: number | null = null;
    if (trimmed.length > 0) {
      const parsed = Number(trimmed);
      if (!Number.isFinite(parsed) || !Number.isInteger(parsed)) {
        setError('Must be a whole number');
        commitGuard.current = false;
        return;
      }
      if (parsed < min) {
        setError(`Must be ≥ ${min}`);
        commitGuard.current = false;
        return;
      }
      if (max !== undefined && parsed > max) {
        setError(`Must be ≤ ${max}`);
        commitGuard.current = false;
        return;
      }
      next = parsed;
    }
    if (next === value) {
      setIsEditing(false);
      commitGuard.current = false;
      return;
    }

    setIsSaving(true);
    try {
      await onSave(next);
      setIsEditing(false);
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'Failed to save'));
    } finally {
      setIsSaving(false);
      commitGuard.current = false;
    }
  };

  const cancel = () => {
    setDraft(value !== null ? String(value) : '');
    setError(null);
    setIsEditing(false);
  };

  if (!isEditing) {
    const hasValue = value !== null;
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsEditing(true)}
        className={cn(
          'w-full rounded px-2 py-1 text-left text-sm transition-colors',
          hasValue ? 'text-foreground' : 'text-muted-foreground',
          !disabled && 'hover:bg-muted/50 cursor-text',
          disabled && 'cursor-default',
          className
        )}
      >
        {renderDisplay
          ? renderDisplay(value)
          : hasValue
            ? value
            : emptyLabel}
      </button>
    );
  }

  return (
    <div className={cn('space-y-1', className)}>
      <Input
        autoFocus
        type="number"
        inputMode="numeric"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void commit();
          }
          if (e.key === 'Escape') {
            e.preventDefault();
            cancel();
          }
        }}
        placeholder={placeholder}
        disabled={isSaving}
        className="h-9 text-sm"
      />
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}

